import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/label';
import api from '../../lib/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const ParametresTab = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  // États pour les différentes sections
  const [generalParams, setGeneralParams] = useState({
    nom_plateforme: 'ALO ADVANCE',
    email_support: 'support@alofinances.com',
    telephone_support: '+224 XXX XX XX XX',
    adresse: 'Conakry, Guinée',
    devise: 'GNF',
    fuseau_horaire: 'Africa/Conakry'
  });

  // Charger les paramètres depuis le backend
  useEffect(() => {
    const loadSystemParams = async () => {
      try {
        setInitialLoading(true);
        const response = await api.get('/admin/system-params');
        
        // Mettre à jour les états avec les données du backend
        setGeneralParams({
          nom_plateforme: response.data.nom_plateforme,
          email_support: response.data.email_support,
          telephone_support: response.data.telephone_support,
          adresse: response.data.adresse,
          devise: response.data.devise,
          fuseau_horaire: response.data.fuseau_horaire
        });
        
        setSecuriteParams({
          duree_session_admin: response.data.duree_session_admin,
          duree_session_entreprise: response.data.duree_session_entreprise,
          duree_session_employe: response.data.duree_session_employe,
          tentatives_connexion_max: response.data.tentatives_connexion_max,
          duree_blocage_compte: response.data.duree_blocage_compte,
          longueur_min_password: response.data.longueur_min_password,
          require_password_special: response.data.require_password_special,
          require_2fa: response.data.require_2fa
        });
        
        setFinancierParams({
          taux_commission_defaut: response.data.taux_commission_defaut,
          taux_commission_min: response.data.taux_commission_min,
          taux_commission_max: response.data.taux_commission_max,
          montant_avance_min: response.data.montant_avance_min,
          montant_avance_max: response.data.montant_avance_max,
          frais_transaction_fixe: response.data.frais_transaction_fixe,
          frais_transaction_pourcentage: response.data.frais_transaction_pourcentage
        });
        
        console.log('✅ Paramètres système chargés:', response.data);
      } catch (error) {
        console.error('❌ Erreur lors du chargement des paramètres:', error);
        toast.error('Erreur lors du chargement des paramètres système');
      } finally {
        setInitialLoading(false);
      }
    };
    
    loadSystemParams();
  }, []);

  const [securiteParams, setSecuriteParams] = useState({
    duree_session_admin: 15,
    duree_session_entreprise: 15,
    duree_session_employe: 30,
    tentatives_connexion_max: 5,
    duree_blocage_compte: 30,
    longueur_min_password: 6,
    require_password_special: false,
    require_2fa: false
  });

  const [financierParams, setFinancierParams] = useState({
    taux_commission_defaut: 5,
    taux_commission_min: 0,
    taux_commission_max: 20,
    montant_avance_min: 10000,
    montant_avance_max: 5000000,
    frais_transaction_fixe: 0,
    frais_transaction_pourcentage: 0
  });

  const [validationParams, setValidationParams] = useState({
    kyb_automatique: false,
    validation_manuelle_requise: true,
    documents_requis_kyb: ['NIF', 'RCCM', 'Statuts'],
    delai_validation_max_jours: 7,
    montant_avance_validation_manuelle: 500000
  });

  const [notificationParams, setNotificationParams] = useState({
    email_notifications: true,
    sms_notifications: false,
    push_notifications: false,
    notif_nouvelle_entreprise: true,
    notif_avance_importante: true,
    notif_alerte_fraude: true
  });

  const handleSave = async (section) => {
    setLoading(true);
    setSaveSuccess(false);
    
    try {
      // Préparer les données à envoyer selon la section
      let dataToSend = {};
      
      switch(section) {
        case 'general':
          dataToSend = generalParams;
          break;
        case 'securite':
          dataToSend = securiteParams;
          break;
        case 'financier':
          dataToSend = financierParams;
          break;
        case 'validation':
          dataToSend = validationParams;
          break;
        case 'notifications':
          dataToSend = notificationParams;
          break;
        default:
          break;
      }
      
      // Appel API pour sauvegarder
      await api.put('/admin/system-params', dataToSend);
      
      toast.success('✅ Paramètres sauvegardés avec succès !');
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('❌ Erreur lors de la sauvegarde des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = (section) => {
    if (window.confirm('Êtes-vous sûr de vouloir réinitialiser ces paramètres aux valeurs par défaut ?')) {
      // Réinitialiser aux valeurs par défaut
      switch(section) {
        case 'general':
          setGeneralParams({
            nom_plateforme: 'ALO ADVANCE',
            email_support: 'support@alofinances.com',
            telephone_support: '+224 XXX XX XX XX',
            adresse: 'Conakry, Guinée',
            devise: 'GNF',
            fuseau_horaire: 'Africa/Conakry'
          });
          break;
        case 'securite':
          setSecuriteParams({
            duree_session_admin: 15,
            duree_session_entreprise: 15,
            duree_session_employe: 30,
            tentatives_connexion_max: 5,
            duree_blocage_compte: 30,
            longueur_min_password: 6,
            require_password_special: false,
            require_2fa: false
          });
          break;
        case 'financier':
          setFinancierParams({
            taux_commission_defaut: 5,
            taux_commission_min: 0,
            taux_commission_max: 20,
            montant_avance_min: 10000,
            montant_avance_max: 5000000,
            frais_transaction_fixe: 0,
            frais_transaction_pourcentage: 0
          });
          break;
        default:
          break;
      }
      alert('✅ Paramètres réinitialisés aux valeurs par défaut');
    }
  };

  return (
    <div className="space-y-6">
      <ToastContainer position="top-right" autoClose={3000} />
      
      {initialLoading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement des paramètres...</p>
          </div>
        </div>
      ) : (
        <>
      {/* Navigation des sections */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            <Button
              variant={activeSection === 'general' ? 'default' : 'outline'}
              onClick={() => setActiveSection('general')}
              size="sm"
            >
              ⚙️ Général
            </Button>
            <Button
              variant={activeSection === 'securite' ? 'default' : 'outline'}
              onClick={() => setActiveSection('securite')}
              size="sm"
            >
              🔒 Sécurité
            </Button>
            <Button
              variant={activeSection === 'financier' ? 'default' : 'outline'}
              onClick={() => setActiveSection('financier')}
              size="sm"
            >
              💰 Financier
            </Button>
            <Button
              variant={activeSection === 'validation' ? 'default' : 'outline'}
              onClick={() => setActiveSection('validation')}
              size="sm"
            >
              ✅ Validation KYB
            </Button>
            <Button
              variant={activeSection === 'notifications' ? 'default' : 'outline'}
              onClick={() => setActiveSection('notifications')}
              size="sm"
            >
              🔔 Notifications
            </Button>
            <Button
              variant={activeSection === 'maintenance' ? 'default' : 'outline'}
              onClick={() => setActiveSection('maintenance')}
              size="sm"
            >
              🛠️ Maintenance
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Message de succès */}
      {saveSuccess && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center">
          <span className="text-green-600 text-2xl mr-3">✅</span>
          <span className="text-green-800 font-medium">Paramètres sauvegardés avec succès !</span>
        </div>
      )}

      {/* Section Général */}
      {activeSection === 'general' && (
        <Card>
          <CardHeader>
            <CardTitle>⚙️ Paramètres Généraux de la Plateforme</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="nom_plateforme">Nom de la Plateforme</Label>
                <Input
                  id="nom_plateforme"
                  value={generalParams.nom_plateforme}
                  onChange={(e) => setGeneralParams({...generalParams, nom_plateforme: e.target.value})}
                />
              </div>
              
              <div>
                <Label htmlFor="devise">Devise</Label>
                <select
                  id="devise"
                  value={generalParams.devise}
                  onChange={(e) => setGeneralParams({...generalParams, devise: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="GNF">Franc Guinéen (GNF)</option>
                  <option value="USD">Dollar US (USD)</option>
                  <option value="EUR">Euro (EUR)</option>
                </select>
              </div>

              <div>
                <Label htmlFor="email_support">Email Support</Label>
                <Input
                  id="email_support"
                  type="email"
                  value={generalParams.email_support}
                  onChange={(e) => setGeneralParams({...generalParams, email_support: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="telephone_support">Téléphone Support</Label>
                <Input
                  id="telephone_support"
                  value={generalParams.telephone_support}
                  onChange={(e) => setGeneralParams({...generalParams, telephone_support: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="adresse">Adresse</Label>
                <Input
                  id="adresse"
                  value={generalParams.adresse}
                  onChange={(e) => setGeneralParams({...generalParams, adresse: e.target.value})}
                />
              </div>

              <div>
                <Label htmlFor="fuseau_horaire">Fuseau Horaire</Label>
                <select
                  id="fuseau_horaire"
                  value={generalParams.fuseau_horaire}
                  onChange={(e) => setGeneralParams({...generalParams, fuseau_horaire: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Africa/Conakry">Conakry (GMT+0)</option>
                  <option value="UTC">UTC</option>
                  <option value="Europe/Paris">Paris (GMT+1)</option>
                </select>
              </div>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <Button onClick={() => handleSave('general')} disabled={loading}>
                {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </Button>
              <Button variant="outline" onClick={() => handleReset('general')}>
                🔄 Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Sécurité */}
      {activeSection === 'securite' && (
        <Card>
          <CardHeader>
            <CardTitle>🔒 Paramètres de Sécurité</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <span className="text-blue-600 text-2xl mr-3">ℹ️</span>
                <div>
                  <p className="text-blue-900 font-medium">Durées de Session</p>
                  <p className="text-blue-800 text-sm mt-1">
                    Configuration actuelle : Admin/Entreprise (15 min), Employé (30 min)
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="duree_session_admin">Durée Session Admin (minutes)</Label>
                <Input
                  id="duree_session_admin"
                  type="number"
                  value={securiteParams.duree_session_admin}
                  onChange={(e) => setSecuriteParams({...securiteParams, duree_session_admin: parseInt(e.target.value)})}
                  min="5"
                  max="120"
                />
                <p className="text-xs text-gray-500 mt-1">Entre 5 et 120 minutes</p>
              </div>

              <div>
                <Label htmlFor="duree_session_entreprise">Durée Session Entreprise (minutes)</Label>
                <Input
                  id="duree_session_entreprise"
                  type="number"
                  value={securiteParams.duree_session_entreprise}
                  onChange={(e) => setSecuriteParams({...securiteParams, duree_session_entreprise: parseInt(e.target.value)})}
                  min="5"
                  max="120"
                />
              </div>

              <div>
                <Label htmlFor="duree_session_employe">Durée Session Employé (minutes)</Label>
                <Input
                  id="duree_session_employe"
                  type="number"
                  value={securiteParams.duree_session_employe}
                  onChange={(e) => setSecuriteParams({...securiteParams, duree_session_employe: parseInt(e.target.value)})}
                  min="5"
                  max="120"
                />
              </div>

              <div>
                <Label htmlFor="tentatives_connexion_max">Tentatives de Connexion Max</Label>
                <Input
                  id="tentatives_connexion_max"
                  type="number"
                  value={securiteParams.tentatives_connexion_max}
                  onChange={(e) => setSecuriteParams({...securiteParams, tentatives_connexion_max: parseInt(e.target.value)})}
                  min="3"
                  max="10"
                />
                <p className="text-xs text-gray-500 mt-1">Avant blocage du compte</p>
              </div>

              <div>
                <Label htmlFor="duree_blocage_compte">Durée Blocage Compte (minutes)</Label>
                <Input
                  id="duree_blocage_compte"
                  type="number"
                  value={securiteParams.duree_blocage_compte}
                  onChange={(e) => setSecuriteParams({...securiteParams, duree_blocage_compte: parseInt(e.target.value)})}
                  min="15"
                  max="1440"
                />
              </div>

              <div>
                <Label htmlFor="longueur_min_password">Longueur Min Mot de Passe</Label>
                <Input
                  id="longueur_min_password"
                  type="number"
                  value={securiteParams.longueur_min_password}
                  onChange={(e) => setSecuriteParams({...securiteParams, longueur_min_password: parseInt(e.target.value)})}
                  min="6"
                  max="20"
                />
              </div>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Caractères Spéciaux Obligatoires</p>
                  <p className="text-sm text-gray-600">Exiger des caractères spéciaux dans les mots de passe</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securiteParams.require_password_special}
                    onChange={(e) => setSecuriteParams({...securiteParams, require_password_special: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Authentification à 2 Facteurs (2FA)</p>
                  <p className="text-sm text-gray-600">Exiger 2FA pour tous les administrateurs</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={securiteParams.require_2fa}
                    onChange={(e) => setSecuriteParams({...securiteParams, require_2fa: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <Button onClick={() => handleSave('securite')} disabled={loading}>
                {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </Button>
              <Button variant="outline" onClick={() => handleReset('securite')}>
                🔄 Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Financier */}
      {activeSection === 'financier' && (
        <Card>
          <CardHeader>
            <CardTitle>💰 Paramètres Financiers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="taux_commission_defaut">Taux Commission Par Défaut (%)</Label>
                <Input
                  id="taux_commission_defaut"
                  type="number"
                  step="0.1"
                  value={financierParams.taux_commission_defaut}
                  onChange={(e) => setFinancierParams({...financierParams, taux_commission_defaut: parseFloat(e.target.value)})}
                  min="0"
                  max="20"
                />
                <p className="text-xs text-gray-500 mt-1">Pour les nouvelles entreprises</p>
              </div>

              <div>
                <Label htmlFor="taux_commission_min">Taux Commission Minimum (%)</Label>
                <Input
                  id="taux_commission_min"
                  type="number"
                  step="0.1"
                  value={financierParams.taux_commission_min}
                  onChange={(e) => setFinancierParams({...financierParams, taux_commission_min: parseFloat(e.target.value)})}
                  min="0"
                  max="20"
                />
              </div>

              <div>
                <Label htmlFor="taux_commission_max">Taux Commission Maximum (%)</Label>
                <Input
                  id="taux_commission_max"
                  type="number"
                  step="0.1"
                  value={financierParams.taux_commission_max}
                  onChange={(e) => setFinancierParams({...financierParams, taux_commission_max: parseFloat(e.target.value)})}
                  min="0"
                  max="100"
                />
              </div>

              <div>
                <Label htmlFor="montant_avance_min">Montant Avance Minimum (GNF)</Label>
                <Input
                  id="montant_avance_min"
                  type="number"
                  value={financierParams.montant_avance_min}
                  onChange={(e) => setFinancierParams({...financierParams, montant_avance_min: parseInt(e.target.value)})}
                  min="0"
                />
              </div>

              <div>
                <Label htmlFor="montant_avance_max">Montant Avance Maximum (GNF)</Label>
                <Input
                  id="montant_avance_max"
                  type="number"
                  value={financierParams.montant_avance_max}
                  onChange={(e) => setFinancierParams({...financierParams, montant_avance_max: parseInt(e.target.value)})}
                  min="0"
                />
              </div>

              <div>
                <Label htmlFor="frais_transaction_fixe">Frais Transaction Fixe (GNF)</Label>
                <Input
                  id="frais_transaction_fixe"
                  type="number"
                  value={financierParams.frais_transaction_fixe}
                  onChange={(e) => setFinancierParams({...financierParams, frais_transaction_fixe: parseInt(e.target.value)})}
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Frais fixe par transaction</p>
              </div>

              <div>
                <Label htmlFor="frais_transaction_pourcentage">Frais Transaction (%)</Label>
                <Input
                  id="frais_transaction_pourcentage"
                  type="number"
                  step="0.01"
                  value={financierParams.frais_transaction_pourcentage}
                  onChange={(e) => setFinancierParams({...financierParams, frais_transaction_pourcentage: parseFloat(e.target.value)})}
                  min="0"
                  max="10"
                />
                <p className="text-xs text-gray-500 mt-1">Frais variable sur montant</p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mt-6">
              <div className="flex items-start">
                <span className="text-yellow-600 text-2xl mr-3">⚠️</span>
                <div>
                  <p className="text-yellow-900 font-medium">Attention</p>
                  <p className="text-yellow-800 text-sm mt-1">
                    Les modifications des taux de commission ne s'appliquent qu'aux nouvelles entreprises. Les entreprises existantes conservent leur taux actuel.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <Button onClick={() => handleSave('financier')} disabled={loading}>
                {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </Button>
              <Button variant="outline" onClick={() => handleReset('financier')}>
                🔄 Réinitialiser
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Validation KYB */}
      {activeSection === 'validation' && (
        <Card>
          <CardHeader>
            <CardTitle>✅ Paramètres de Validation KYB/KYC</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Validation KYB Automatique</p>
                  <p className="text-sm text-gray-600">Approuver automatiquement les nouvelles entreprises</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={validationParams.kyb_automatique}
                    onChange={(e) => setValidationParams({...validationParams, kyb_automatique: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">Validation Manuelle Requise</p>
                  <p className="text-sm text-gray-600">Exiger une validation manuelle par un admin</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={validationParams.validation_manuelle_requise}
                    onChange={(e) => setValidationParams({...validationParams, validation_manuelle_requise: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t">
              <div>
                <Label htmlFor="delai_validation_max_jours">Délai Max de Validation (jours)</Label>
                <Input
                  id="delai_validation_max_jours"
                  type="number"
                  value={validationParams.delai_validation_max_jours}
                  onChange={(e) => setValidationParams({...validationParams, delai_validation_max_jours: parseInt(e.target.value)})}
                  min="1"
                  max="30"
                />
                <p className="text-xs text-gray-500 mt-1">Délai pour valider une demande</p>
              </div>

              <div>
                <Label htmlFor="montant_avance_validation_manuelle">Montant Validation Manuelle (GNF)</Label>
                <Input
                  id="montant_avance_validation_manuelle"
                  type="number"
                  value={validationParams.montant_avance_validation_manuelle}
                  onChange={(e) => setValidationParams({...validationParams, montant_avance_validation_manuelle: parseInt(e.target.value)})}
                  min="0"
                />
                <p className="text-xs text-gray-500 mt-1">Au-delà, validation admin requise</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <Label>Documents KYB Requis</Label>
              <div className="space-y-2 mt-2">
                {validationParams.documents_requis_kyb.map((doc, index) => (
                  <div key={index} className="flex items-center p-3 bg-blue-50 rounded-lg">
                    <span className="text-blue-600 mr-2">📄</span>
                    <span className="text-blue-900 font-medium">{doc}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <Button onClick={() => handleSave('validation')} disabled={loading}>
                {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Notifications */}
      {activeSection === 'notifications' && (
        <Card>
          <CardHeader>
            <CardTitle>🔔 Paramètres de Notifications</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">📧 Notifications Email</p>
                  <p className="text-sm text-gray-600">Envoyer des notifications par email</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationParams.email_notifications}
                    onChange={(e) => setNotificationParams({...notificationParams, email_notifications: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">💬 Notifications SMS</p>
                  <p className="text-sm text-gray-600">Envoyer des notifications par SMS</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationParams.sms_notifications}
                    onChange={(e) => setNotificationParams({...notificationParams, sms_notifications: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">📱 Push Notifications</p>
                  <p className="text-sm text-gray-600">Activer les notifications push (PWA)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={notificationParams.push_notifications}
                    onChange={(e) => setNotificationParams({...notificationParams, push_notifications: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>

            <div className="pt-4 border-t">
              <p className="font-medium text-gray-900 mb-4">Événements à Notifier</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div>
                    <p className="font-medium text-blue-900">🏢 Nouvelle Entreprise</p>
                    <p className="text-sm text-blue-700">Notification lors d'une nouvelle inscription</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationParams.notif_nouvelle_entreprise}
                      onChange={(e) => setNotificationParams({...notificationParams, notif_nouvelle_entreprise: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                  <div>
                    <p className="font-medium text-orange-900">💰 Avance Importante</p>
                    <p className="text-sm text-orange-700">Notification pour avances &gt; 500k GNF</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationParams.notif_avance_importante}
                      onChange={(e) => setNotificationParams({...notificationParams, notif_avance_importante: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                  <div>
                    <p className="font-medium text-red-900">🚨 Alerte Fraude</p>
                    <p className="text-sm text-red-700">Notification en cas d'activité suspecte</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={notificationParams.notif_alerte_fraude}
                      onChange={(e) => setNotificationParams({...notificationParams, notif_alerte_fraude: e.target.checked})}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
              </div>
            </div>

            <div className="flex space-x-3 pt-4 border-t">
              <Button onClick={() => handleSave('notifications')} disabled={loading}>
                {loading ? '⏳ Enregistrement...' : '💾 Enregistrer'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Section Maintenance */}
      {activeSection === 'maintenance' && (
        <Card>
          <CardHeader>
            <CardTitle>🛠️ Maintenance Système</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-blue-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="text-4xl mb-3">🗄️</div>
                    <h3 className="font-medium text-gray-900 mb-2">Sauvegarde Base de Données</h3>
                    <p className="text-sm text-gray-600 mb-4">Créer une sauvegarde complète de la base de données</p>
                    <Button className="w-full">
                      💾 Créer une Sauvegarde
                    </Button>
                    <p className="text-xs text-gray-500 mt-3">
                      Dernière sauvegarde : Jamais
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-green-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="text-4xl mb-3">📊</div>
                    <h3 className="font-medium text-gray-900 mb-2">Rapport Système</h3>
                    <p className="text-sm text-gray-600 mb-4">Générer un rapport complet du système</p>
                    <Button className="w-full bg-green-600 hover:bg-green-700">
                      📄 Générer Rapport
                    </Button>
                    <p className="text-xs text-gray-500 mt-3">
                      Format : PDF
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-orange-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="text-4xl mb-3">🧹</div>
                    <h3 className="font-medium text-gray-900 mb-2">Nettoyage Cache</h3>
                    <p className="text-sm text-gray-600 mb-4">Vider le cache et les fichiers temporaires</p>
                    <Button className="w-full bg-orange-600 hover:bg-orange-700">
                      🗑️ Nettoyer le Cache
                    </Button>
                    <p className="text-xs text-gray-500 mt-3">
                      Espace occupé : ~ 0 MB
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-2 border-red-200">
                <CardContent className="p-6">
                  <div className="text-center">
                    <div className="text-4xl mb-3">🔄</div>
                    <h3 className="font-medium text-gray-900 mb-2">Redémarrage Services</h3>
                    <p className="text-sm text-gray-600 mb-4">Redémarrer tous les services backend</p>
                    <Button className="w-full bg-red-600 hover:bg-red-700">
                      ⚠️ Redémarrer Services
                    </Button>
                    <p className="text-xs text-red-600 mt-3">
                      ⚠️ Provoque une interruption temporaire
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
              <div className="flex items-start">
                <span className="text-blue-600 text-2xl mr-3">ℹ️</span>
                <div>
                  <p className="text-blue-900 font-medium">Informations Système</p>
                  <div className="text-blue-800 text-sm mt-2 space-y-1">
                    <p>• Version: 1.0.0</p>
                    <p>• Environnement: Production</p>
                    <p>• Statut Services: ✅ Tous opérationnels</p>
                    <p>• Base de données: MongoDB (connectée)</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      </>
      )}
    </div>
  );
};

export default ParametresTab;
